import type { Lang } from '../i18n'
import { de } from './i18n/de'
import { es } from './i18n/es'
import { th } from './i18n/th'
import { zh } from './i18n/zh'

/**
 * Copy for «ABV System» — the interactive universe mode.
 * All six languages are complete: RU and EN live here, DE/ES/ZH/TH in ./i18n/.
 */

export type TeamId = 'analytics' | 'ai' | 'development' | 'testing' | 'devops'

export type UniverseChrome = {
  open: string
  openShort: string
  classic: string
  overview: string
  home: string
  route: string
  next: string
  back: string
  fly: string
  flying: string
  team: string
  projects: string
  newProject: string
  mission: string
  missionHint: string
  close: string
  news: string
  log: string
  logHint: string
  logDone: string
  logDoneText: string
}

export type LangEgg = {
  /** Shouted by mission control on every rocket launch. */
  launch: string
  /** A small floating object with a secret line when clicked. */
  object: 'tube' | 'boot' | 'pretzel' | 'star' | 'rabbit' | 'lantern'
  label: string
  secret: string
  /** Subtle per-language nebula tint for the background scene. */
  nebula: [string, string, string]
}

export type TeamPlanetCopy = {
  name: string
  tagline: string
  about: string
  facts: string[]
  soon: string
}

/** `{name}`, `{n}`, `{total}` and `{text}` are filled in at runtime, so quotes and colons follow each language. */
export type MissionCopy = {
  title: string
  intro: string
  progress: string
  /** Shown on a locked stage; `{name}` is the stage to finish first. */
  locked: string
  goTo: string
  done: string
  continueTo: string
  analytics: { task: string; cards: Array<{ id: string; label: string }>; pick: string; confirm: string }
  ai: { task: string; generating: string; choose: string; quote: string; wishes: Record<string, string[]>; fallback: string[] }
  development: { task: string; backgrounds: string[]; planets: string[]; bg: string; planet: string; build: string }
  testing: { task: string; found: string; bugs: [string, string, string]; allFound: string }
  devops: { task: string; countdown: string; launch: string; released: string }
  postcard: { title: string; nameLabel: string; namePlaceholder: string; from: string; download: string; share: string; copied: string; again: string; greeting: string }
}

export type BriefCopy = {
  title: string
  intro: string
  steps: [string, string, string, string]
  destination: string
  destinations: Array<{ id: string; label: string; hint: string }>
  cargo: string
  cargoItems: string[]
  cargoNote: string
  timeline: string
  timelines: string[]
  crew: string
  name: string
  namePlaceholder: string
  email: string
  emailPlaceholder: string
  message: string
  /** Shown under the step indicator: explains the red asterisk. */
  requiredNote: string
  nameError: string
  emailError: string
  /** Inline hints when «Next» is pressed before a choice is made (steps 1 and 3). */
  pickDestination: string
  pickTimeline: string
  next: string
  back: string
  launch: string
  sending: string
  sent: string
  /** Added to the success message when the local preview server did not send a real email. */
  previewNote: string
  error: string
  rateLimited: string
  summary: string
}

export type CareersPlanetCopy = { name: string; lead: string; mission: string }

/**
 * Wayfinding: overview choices, first-visit coach and the guided tour.
 * `{n}`, `{total}` and `{name}` are filled in at runtime.
 */
export type GuideCopy = {
  title: string
  lead: string
  tour: string
  tourText: string
  recommended: string
  resume: string
  resumeText: string
  missionText: string
  missionProgress: string
  projects: string
  projectsText: string
  request: string
  requestText: string
  meditate: string
  allPlanets: string
  coachKicker: string
  coachText: string
  coachNote: string
  coachTour: string
  coachSelf: string
  help: string
  stop: string
  next: string
  back: string
  end: string
  finish: string
  upNext: string
  fly: string
  visited: string
  missionCount: string
  stageDone: string
  stagePending: string
  /** Tour «Next» button with the next stop's name. */
  nextTo: string
  /** Panel button to the next planet on the route. */
  nextPlanet: string
  /** Accessible name of a planet label or chip. */
  flyTo: string
  /** Appends a short note in brackets: `{text} ({note})`. */
  note: string
  /** A mission stage with its status, e.g. in the mission popover. */
  stageLabel: string
}

export type LongCopy = {
  home: { title: string; lead: string; cta: string }
  team: Record<TeamId, TeamPlanetCopy>
  projects: Record<'platform' | 'grid' | 'panel', string>
  careers: CareersPlanetCopy
  mission: MissionCopy
  brief: BriefCopy
  guide: GuideCopy
}

export type UniverseCopy = {
  chrome: UniverseChrome
  egg: LangEgg
  home: { title: string; lead: string; cta: string }
  team: Record<TeamId, TeamPlanetCopy>
  projects: Record<'platform' | 'grid' | 'panel', string>
  careers: CareersPlanetCopy
  mission: MissionCopy
  brief: BriefCopy
  guide: GuideCopy
}

/** Fills `{key}` placeholders in a copy string. */
export function fill(text: string, values: Record<string, string | number>) {
  return text.replace(/\{(\w+)\}/g, (match, key: string) => (key in values ? String(values[key]) : match))
}

const chrome: Record<Lang, UniverseChrome> = {
  ru: { open: 'Полететь по системе ABV', openShort: 'Полёт', classic: 'Обычный режим', overview: 'Вся система', home: 'Планета ABV', route: 'Маршрут', next: 'Следующая планета', back: 'Назад', fly: 'Лететь', flying: 'Летим…', team: 'Луны команды', projects: 'Планеты проектов', newProject: 'Ваш проект', mission: 'Миссия', missionHint: 'Выполните задания на 5 лунах команды — и получите открытку', close: 'Закрыть', news: 'Новости отдела', log: 'Бортовой журнал', logHint: 'Сколько планет вы уже открыли', logDone: 'Вся система открыта!', logDoneText: 'Вы облетели все планеты ABV. Теперь вы знаете о нас всё — самое время рассказать о вашей задаче.' },
  en: { open: 'Fly through the ABV system', openShort: 'Fly', classic: 'Classic view', overview: 'Whole system', home: 'Planet ABV', route: 'Route', next: 'Next planet', back: 'Back', fly: 'Fly', flying: 'Flying…', team: 'Team moons', projects: 'Project planets', newProject: 'Your project', mission: 'Mission', missionHint: 'Complete the tasks on the 5 team moons to earn a postcard', close: 'Close', news: 'Team news', log: 'Logbook', logHint: 'How many planets you have discovered', logDone: 'The whole system is discovered!', logDoneText: 'You have visited every ABV planet. Now you know all about us — time to tell us about your task.' },
  de: { open: 'Durch das ABV-System fliegen', openShort: 'Flug', classic: 'Klassische Ansicht', overview: 'Ganzes System', home: 'Planet ABV', route: 'Route', next: 'Nächster Planet', back: 'Zurück', fly: 'Fliegen', flying: 'Unterwegs…', team: 'Team-Monde', projects: 'Projekt-Planeten', newProject: 'Ihr Projekt', mission: 'Mission', missionHint: 'Lösen Sie die Aufgaben auf den 5 Team-Monden und erhalten Sie eine Postkarte', close: 'Schließen', news: 'Team-News', log: 'Logbuch', logHint: 'So viele Planeten haben Sie schon entdeckt', logDone: 'Das ganze System ist entdeckt!', logDoneText: 'Sie haben alle ABV-Planeten besucht. Jetzt kennen Sie uns — erzählen Sie uns von Ihrer Aufgabe.' },
  es: { open: 'Volar por el sistema ABV', openShort: 'Volar', classic: 'Vista clásica', overview: 'Todo el sistema', home: 'Planeta ABV', route: 'Ruta', next: 'Siguiente planeta', back: 'Atrás', fly: 'Volar', flying: 'Volando…', team: 'Lunas del equipo', projects: 'Planetas de proyectos', newProject: 'Tu proyecto', mission: 'Misión', missionHint: 'Completa las tareas de las 5 lunas del equipo y consigue una postal', close: 'Cerrar', news: 'Noticias del equipo', log: 'Bitácora', logHint: 'Cuántos planetas has descubierto', logDone: '¡Has descubierto todo el sistema!', logDoneText: 'Has visitado todos los planetas de ABV. Ya lo sabes todo sobre nosotros: es el momento de contarnos tu reto.' },
  zh: { open: '飞越 ABV 星系', openShort: '起飞', classic: '经典视图', overview: '整个星系', home: 'ABV 星球', route: '航线', next: '下一颗星球', back: '返回', fly: '出发', flying: '飞行中…', team: '团队卫星', projects: '项目星球', newProject: '您的项目', mission: '任务', missionHint: '完成 5 颗团队卫星上的任务，领取一张明信片', close: '关闭', news: '团队动态', log: '航行日志', logHint: '您已探索的星球数量', logDone: '整个星系已探索完毕！', logDoneText: '您已访问 ABV 的所有星球。现在您已了解我们——是时候告诉我们您的需求了。' },
  th: { open: 'บินสำรวจระบบ ABV', openShort: 'บิน', classic: 'มุมมองปกติ', overview: 'ทั้งระบบ', home: 'ดาว ABV', route: 'เส้นทาง', next: 'ดาวดวงถัดไป', back: 'ย้อนกลับ', fly: 'บิน', flying: 'กำลังบิน…', team: 'ดวงจันทร์ของทีม', projects: 'ดาวแห่งโปรเจกต์', newProject: 'โปรเจกต์ของคุณ', mission: 'ภารกิจ', missionHint: 'ทำภารกิจบนดวงจันทร์ของทีมให้ครบทั้ง 5 ดวงแล้วรับโปสการ์ด', close: 'ปิด', news: 'ข่าวจากทีม', log: 'บันทึกการบิน', logHint: 'จำนวนดาวที่คุณค้นพบแล้ว', logDone: 'สำรวจครบทั้งระบบแล้ว!', logDoneText: 'คุณเยี่ยมชมดาวของ ABV ครบทุกดวงแล้ว ตอนนี้คุณรู้จักเราดีแล้ว — ถึงเวลาเล่าโจทย์ของคุณให้เราฟัง' },
}

const eggs: Record<Lang, LangEgg> = {
  ru: { launch: 'Поехали!', object: 'tube', label: 'Тюбик из космического меню', secret: 'Меню космонавта: борщ в тюбике. Приятного полёта!', nebula: ['#1d3470', '#43306f', '#153a5c'] },
  en: { launch: 'Houston, we have no problems', object: 'boot', label: 'An astronaut’s footprint', secret: 'One small click for you, one giant leap for your business.', nebula: ['#153f5c', '#2d3a7a', '#1a4a52'] },
  de: { launch: 'Los geht’s!', object: 'pretzel', label: 'Eine Brezel im Orbit', secret: 'Auch im All gilt: erst die Brezel, dann die Mission.', nebula: ['#2a2f6a', '#3a2a5e', '#1d3a5c'] },
  es: { launch: '¡Despegamos!', object: 'star', label: 'Una estrella fugaz', secret: '¡Una estrella fugaz! Pide un deseo… nosotros lo convertimos en software.', nebula: ['#4a2a4f', '#2d3470', '#5a3322'] },
  zh: { launch: '出发！', object: 'rabbit', label: '玉兔', secret: '玉兔也来帮忙啦！祝您的项目顺利升空。', nebula: ['#5a1f2e', '#3a2a6e', '#5a3a18'] },
  th: { launch: 'ไปกันเลย!', object: 'lantern', label: 'โคมลอย', secret: 'ขอให้ทุกความฝันของคุณลอยสูงเหมือนโคมลอย', nebula: ['#4a2a5e', '#5a3a1a', '#1d3470'] },
}

const ruTeam: Record<TeamId, TeamPlanetCopy> = {
  analytics: {
    name: 'Системная аналитика',
    tagline: 'Переводим бизнес на язык систем',
    about: 'Здесь начинается каждый проект. Аналитики разбираются, как на самом деле работает бизнес клиента, и превращают это в требования, схемы процессов и контракты API, по которым команда строит систему без догадок.',
    facts: ['Сначала понимаем процесс — потом пишем код', 'Требования, схемы процессов, прототипы интерфейсов', 'Контракты API согласуем до начала разработки'],
    soon: 'Скоро здесь появятся новости и находки команды аналитики',
  },
  ai: {
    name: 'ИИ-инжиниринг',
    tagline: 'Встраиваем интеллект в рабочие процессы',
    about: 'Команда проектирует ИИ-агентов, поиск по данным компании, распознавание документов и подключение сервисов к ассистентам по стандарту MCP — так, чтобы ИИ работал на реальный процесс, а не ради демонстрации.',
    facts: ['ИИ-агенты и MCP-серверы', 'Поиск и RAG по данным компании', 'Распознавание документов, сканов и спецификаций'],
    soon: 'Скоро здесь появятся эксперименты и новости ИИ-команды',
  },
  development: {
    name: 'Разработка',
    tagline: 'Строим системы, которые работают годами',
    about: 'Backend и frontend разработчики собирают систему по согласованным контрактам: код-ревью, контроль версий и архитектура, рассчитанная на рост нагрузки и новых функций.',
    facts: ['Backend и frontend в одной команде', 'Обязательное код-ревью', 'Архитектура под высокие нагрузки'],
    soon: 'Скоро здесь появятся новости и технологические фишки разработчиков',
  },
  testing: {
    name: 'Тестирование',
    tagline: 'Ловим ошибки до того, как их увидит клиент',
    about: 'QA-инженеры проверяют функции, интеграции и регрессию, автоматизируют критичные сценарии и проводят нагрузочное тестирование перед каждым выходом в прод.',
    facts: ['Функциональное, интеграционное и регрессионное тестирование', 'Автотесты для критичных сценариев', 'Нагрузочное тестирование перед релизом'],
    soon: 'Скоро здесь появятся новости и челленджи команды тестирования',
  },
  devops: {
    name: 'DevOps',
    tagline: 'Выпускаем быстро и стабильно',
    about: 'Автоматическая сборка и развёртывание на тестовые и боевые среды, мониторинг, безопасность окружений и масштабирование — чтобы новые версии выходили часто и без сюрпризов.',
    facts: ['CI/CD для всех сред', 'Мониторинг и контроль ошибок', 'Кластеризация и балансировка нагрузки'],
    soon: 'Скоро здесь появятся новости и лайфхаки DevOps-команды',
  },
}

const enTeam: Record<TeamId, TeamPlanetCopy> = {
  analytics: {
    name: 'Systems analysis',
    tagline: 'Translating business into systems',
    about: 'Every project starts here. Analysts learn how the client’s business really works and turn it into requirements, process diagrams and API contracts, so the team builds without guesswork.',
    facts: ['Understand the process first — then write code', 'Requirements, process maps, interface prototypes', 'API contracts agreed before development starts'],
    soon: 'News and discoveries from the analysis team are coming soon',
  },
  ai: {
    name: 'AI engineering',
    tagline: 'Building intelligence into real workflows',
    about: 'The team designs AI agents, search over company data, document recognition and MCP connections to assistants — so AI serves a real process, not a demo.',
    facts: ['AI agents and MCP servers', 'Search and RAG over company data', 'Recognition of documents, scans and specs'],
    soon: 'Experiments and news from the AI team are coming soon',
  },
  development: {
    name: 'Development',
    tagline: 'Building systems that run for years',
    about: 'Backend and frontend engineers build against agreed contracts: code review, version control and an architecture designed for growing load and new features.',
    facts: ['Backend and frontend in one team', 'Mandatory code review', 'Architecture for high load'],
    soon: 'News and tech highlights from our developers are coming soon',
  },
  testing: {
    name: 'Testing',
    tagline: 'Catching bugs before customers do',
    about: 'QA engineers test features, integrations and regressions, automate critical scenarios and run load tests before every production release.',
    facts: ['Functional, integration and regression testing', 'Automated tests for critical flows', 'Load testing before release'],
    soon: 'News and challenges from the QA team are coming soon',
  },
  devops: {
    name: 'DevOps',
    tagline: 'Shipping fast and stable',
    about: 'Automated builds and deployment to staging and production, monitoring, environment security and scaling — so new versions ship often and without surprises.',
    facts: ['CI/CD across every environment', 'Monitoring and error tracking', 'Clustering and load balancing'],
    soon: 'News and tips from the DevOps team are coming soon',
  },
}

const ruMission: MissionCopy = {
  title: 'Миссия «От идеи до релиза»',
  intro: 'Пройдите весь путь, как проходит его каждый наш проект: аналитика → ИИ → разработка → тестирование → запуск. В финале — открытка с пожеланием, которую можно скачать или отправить коллеге.',
  progress: 'Этапы миссии',
  locked: 'Этот этап откроется после планеты «{name}»',
  goTo: 'Лететь туда',
  done: 'Этап пройден!',
  continueTo: 'Следующий этап: {name}',
  analytics: {
    task: 'Задача аналитика: выберите 3 карточки — какой должна получиться открытка.',
    cards: [
      { id: 'calm', label: 'Спокойствие' }, { id: 'energy', label: 'Энергия' }, { id: 'luck', label: 'Удача' },
      { id: 'team', label: 'Для команды' }, { id: 'bold', label: 'Смелые идеи' }, { id: 'joy', label: 'Радость' },
    ],
    pick: 'Выбрано: {n} / {total}',
    confirm: 'Утвердить требования',
  },
  ai: {
    task: 'ИИ подобрал пожелания по вашим требованиям. Выберите лучшее.',
    generating: 'Модель думает…',
    choose: 'Выбрать',
    quote: '«{text}»',
    wishes: {
      calm: ['Пусть день будет спокойным, а задачи — решаемыми.'],
      energy: ['Заряда на весь день — и ещё немного на вечер!'],
      luck: ['Пусть сегодня всё сложится с первой попытки.'],
      team: ['Отличного дня вам и вашей команде!'],
      bold: ['Пусть смелые идеи сегодня превращаются в планы.'],
      joy: ['Больше поводов улыбнуться — прямо сегодня!'],
    },
    fallback: ['Хорошего дня и лёгких задач!'],
  },
  development: {
    task: 'Соберите открытку из готовых компонентов.',
    backgrounds: ['Глубокий космос', 'Северное сияние', 'Закат на орбите'],
    planets: ['Планета ABV', 'Кольцевой гигант', 'Голубая луна'],
    bg: 'Фон',
    planet: 'Планета',
    build: 'Собрать сборку',
  },
  testing: {
    task: 'Найдите 3 бага в сборке — нажмите на них прямо на открытке.',
    found: 'Найдено: {n} / {total}',
    bugs: ['опечатка в тексте', 'съехавшая планета', 'лишняя красная звезда'],
    allFound: 'Все баги исправлены — сборка готова к релизу',
  },
  devops: {
    task: 'Всё проверено. Запускаем релиз!',
    countdown: 'Обратный отсчёт',
    launch: 'Запустить релиз',
    released: 'Релиз прошёл успешно!',
  },
  postcard: {
    title: 'Ваша открытка готова',
    nameLabel: 'Для кого открытка',
    namePlaceholder: 'Имя',
    from: 'с любовью из системы ABV Develop',
    download: 'Скачать открытку',
    share: 'Скопировать ссылку на сайт',
    copied: 'Ссылка скопирована',
    again: 'Пройти ещё раз',
    greeting: 'Хорошего дня',
  },
}

const enMission: MissionCopy = {
  title: 'Mission “From idea to release”',
  intro: 'Take the same path every one of our projects takes: analysis → AI → development → testing → launch. At the end you get a postcard with a wish to download or send to a colleague.',
  progress: 'Mission stages',
  locked: 'This stage unlocks after “{name}”',
  goTo: 'Fly there',
  done: 'Stage complete!',
  continueTo: 'Next stage: {name}',
  analytics: {
    task: 'Analyst task: pick 3 cards — what should the postcard feel like?',
    cards: [
      { id: 'calm', label: 'Calm' }, { id: 'energy', label: 'Energy' }, { id: 'luck', label: 'Luck' },
      { id: 'team', label: 'For the team' }, { id: 'bold', label: 'Bold ideas' }, { id: 'joy', label: 'Joy' },
    ],
    pick: 'Selected: {n} / {total}',
    confirm: 'Approve requirements',
  },
  ai: {
    task: 'The AI drafted wishes from your requirements. Pick the best one.',
    generating: 'The model is thinking…',
    choose: 'Choose',
    quote: '“{text}”',
    wishes: {
      calm: ['May your day be calm and your tasks solvable.'],
      energy: ['Energy for the whole day — and a little extra for the evening!'],
      luck: ['May everything work on the first try today.'],
      team: ['A great day to you and your team!'],
      bold: ['May bold ideas turn into plans today.'],
      joy: ['More reasons to smile — starting today!'],
    },
    fallback: ['Have a great day and easy tasks!'],
  },
  development: {
    task: 'Assemble the postcard from ready-made components.',
    backgrounds: ['Deep space', 'Aurora', 'Orbital sunset'],
    planets: ['Planet ABV', 'Ringed giant', 'Blue moon'],
    bg: 'Background',
    planet: 'Planet',
    build: 'Run the build',
  },
  testing: {
    task: 'Find 3 bugs in the build — tap them right on the postcard.',
    found: 'Found: {n} / {total}',
    bugs: ['a typo in the text', 'a misplaced planet', 'a stray red star'],
    allFound: 'All bugs fixed — the build is ready for release',
  },
  devops: {
    task: 'Everything is tested. Let’s ship it!',
    countdown: 'Countdown',
    launch: 'Launch the release',
    released: 'Release successful!',
  },
  postcard: {
    title: 'Your postcard is ready',
    nameLabel: 'Who is it for',
    namePlaceholder: 'Name',
    from: 'with love from the ABV Develop system',
    download: 'Download postcard',
    share: 'Copy the website link',
    copied: 'Link copied',
    again: 'Play again',
    greeting: 'Have a great day',
  },
}

const ruBrief: BriefCopy = {
  title: 'Полётное задание',
  intro: 'Расскажите о задаче в четыре шага — заявка улетит нашей команде, а мы вернёмся с ответом.',
  steps: ['Назначение', 'Груз', 'Сроки', 'Экипаж'],
  destination: 'Какая система нужна?',
  destinations: [
    { id: 'erp', label: 'ERP / CRM', hint: 'процессы, учёт, документы' },
    { id: 'catalog', label: 'Каталог и поиск', hint: 'B2B-портал, большие ассортименты' },
    { id: 'ai', label: 'ИИ-агенты', hint: 'автоматизация, MCP, распознавание' },
    { id: 'engineering', label: 'Инженерная система', hint: 'энергетика, конфигураторы, GIS' },
    { id: 'other', label: 'Другое', hint: 'расскажем вместе' },
  ],
  cargo: 'Что важно в проекте? Можно выбрать несколько',
  cargoItems: ['Автоматизировать процессы', 'Интеграции с другими системами', 'Высокие нагрузки', 'ИИ и работа с данными', 'Развитие существующей системы', 'Запуск с нуля'],
  cargoNote: 'Пара слов о задаче (необязательно)',
  timeline: 'Когда нужен старт?',
  timelines: ['Как можно скорее', '1–3 месяца', 'Через полгода', 'Пока изучаем'],
  crew: 'Кто на связи?',
  name: 'Имя',
  namePlaceholder: 'Например, Анна',
  email: 'Рабочая почта',
  emailPlaceholder: 'name@company.com',
  message: 'Комментарий',
  requiredNote: '* — обязательные поля',
  nameError: 'Укажите, как к вам обращаться',
  emailError: 'Проверьте адрес почты — например, name@company.com',
  pickDestination: 'Выберите один вариант, чтобы продолжить',
  pickTimeline: 'Выберите срок, чтобы продолжить',
  next: 'Дальше',
  back: 'Назад',
  launch: 'Отправить заявку',
  sending: 'Отправляем…',
  sent: 'Заявка на орбите! Мы ответим на почту в ближайшее время.',
  previewNote: 'Это предпросмотр: письмо не отправлялось',
  error: 'Не удалось отправить. Попробуйте ещё раз или напишите на sales@abvdevelop.com.',
  rateLimited: 'Слишком много заявок подряд. Попробуйте через 15 минут или напишите на sales@abvdevelop.com.',
  summary: 'Полётное задание с сайта',
}

const enBrief: BriefCopy = {
  title: 'Flight plan',
  intro: 'Describe your task in four steps — the request flies to our team, and we get back to you.',
  steps: ['Destination', 'Cargo', 'Timing', 'Crew'],
  destination: 'What kind of system do you need?',
  destinations: [
    { id: 'erp', label: 'ERP / CRM', hint: 'processes, accounting, documents' },
    { id: 'catalog', label: 'Catalog & search', hint: 'B2B portal, large assortments' },
    { id: 'ai', label: 'AI agents', hint: 'automation, MCP, recognition' },
    { id: 'engineering', label: 'Engineering system', hint: 'energy, configurators, GIS' },
    { id: 'other', label: 'Something else', hint: 'let’s figure it out together' },
  ],
  cargo: 'What matters in the project? Pick any',
  cargoItems: ['Automate processes', 'Integrations with other systems', 'High load', 'AI and data', 'Evolve an existing system', 'Start from scratch'],
  cargoNote: 'A few words about the task (optional)',
  timeline: 'When do you want to start?',
  timelines: ['As soon as possible', 'In 1–3 months', 'In about six months', 'Still exploring'],
  crew: 'Who should we contact?',
  name: 'Name',
  namePlaceholder: 'e.g. Anna',
  email: 'Work email',
  emailPlaceholder: 'name@company.com',
  message: 'Comment',
  requiredNote: '* Required fields',
  nameError: 'Please tell us what to call you',
  emailError: 'Please check your email address, e.g. name@company.com',
  pickDestination: 'Choose an option to continue',
  pickTimeline: 'Choose a timeframe to continue',
  next: 'Next',
  back: 'Back',
  launch: 'Send request',
  sending: 'Sending…',
  sent: 'Your request is in orbit! We will reply by email shortly.',
  previewNote: 'Preview mode: no email was sent',
  error: 'We couldn’t send your request. Please try again or email sales@abvdevelop.com.',
  rateLimited: 'Too many requests in a row. Please try again in 15 minutes or email sales@abvdevelop.com.',
  summary: 'Flight plan from the website',
}

const ruGuide: GuideCopy = {
  title: 'Куда летим?',
  lead: 'Нажмите на любую планету — или выберите маршрут:',
  tour: 'Экскурсия по системе',
  tourText: 'Ракета сама проведёт вас по {total} остановкам — нажимайте только «Дальше»',
  recommended: 'Рекомендуем',
  resume: 'Продолжить экскурсию',
  resumeText: 'Вы прервались на остановке {n} из {total}',
  missionText: '5 мини-заданий на лунах команды, в финале — открытка',
  missionProgress: 'Пройдено {n} из {total}',
  projects: 'Проекты',
  projectsText: 'Три системы, которые мы построили',
  request: 'Оставить заявку',
  requestText: 'Расскажите о задаче за 4 шага — ответим на почту',
  meditate: 'Медитация — передохнуть в космосе',
  allPlanets: 'Все планеты',
  coachKicker: 'Как это работает',
  coachText: 'Нажмите на любую планету — и ракета отвезёт вас туда',
  coachNote: 'Не знаете, с чего начать? Экскурсия покажет всю систему за {total} остановок — нажимайте только «Дальше».',
  coachTour: 'Начать экскурсию',
  coachSelf: 'Сначала осмотрюсь',
  help: 'Подсказка: как здесь всё устроено',
  stop: 'Остановка {n} из {total}',
  next: 'Дальше',
  back: 'Назад',
  end: 'Завершить экскурсию',
  finish: 'Финиш — вы облетели всю систему!',
  upNext: 'Следующая остановка: {name}',
  fly: 'Лететь',
  visited: 'вы уже были здесь',
  missionCount: 'Миссия: {n} из {total}',
  stageDone: 'пройдено',
  stagePending: 'ещё не пройдено',
  nextTo: 'Дальше: {name}',
  nextPlanet: 'Следующая планета: {name}',
  flyTo: 'Лететь: {name}',
  note: '{text} ({note})',
  stageLabel: '{name} — {status}',
}

const enGuide: GuideCopy = {
  title: 'Where to?',
  lead: 'Choose any planet — or pick a route:',
  tour: 'System tour',
  tourText: 'The rocket guides you through {total} stops — you just press “Next”',
  recommended: 'Recommended',
  resume: 'Continue the tour',
  resumeText: 'You paused at stop {n} of {total}',
  missionText: '5 mini-tasks on the team moons — and a postcard at the end',
  missionProgress: '{n} of {total} done',
  projects: 'Projects',
  projectsText: 'Three systems we have built',
  request: 'Send a request',
  requestText: 'Describe your task in 4 steps — we’ll reply by email',
  meditate: 'Meditation — take a breather in space',
  allPlanets: 'All planets',
  coachKicker: 'How it works',
  coachText: 'Choose any planet and the rocket will take you there',
  coachNote: 'Not sure where to start? The tour shows the whole system in {total} stops — you just press “Next”.',
  coachTour: 'Start the tour',
  coachSelf: 'I’ll explore on my own',
  help: 'Help: how this works',
  stop: 'Stop {n} of {total}',
  next: 'Next',
  back: 'Back',
  end: 'End tour',
  finish: 'That’s it — you’ve toured the whole system!',
  upNext: 'Next stop: {name}',
  fly: 'Fly',
  visited: 'already visited',
  missionCount: 'Mission: {n} of {total}',
  stageDone: 'done',
  stagePending: 'not done yet',
  nextTo: 'Next: {name}',
  nextPlanet: 'Next planet: {name}',
  flyTo: 'Fly to {name}',
  note: '{text} ({note})',
  stageLabel: '{name} — {status}',
}

const ruCopy: LongCopy = {
  careers: { name: 'Открытый космос', lead: 'Планета для тех, кто хочет строить системы вместе с нами. Каждая вакансия — отдельная миссия.', mission: 'Принять миссию →' },
  home: { title: 'Планета ABV Develop', lead: 'Здесь живёт наша команда и компетенции. Вокруг — луны отделов, дальше — планеты проектов, которые мы построили. Выберите, куда лететь.', cta: 'Отправить полётное задание' },
  team: ruTeam,
  projects: { platform: 'Платформа дистрибуции', grid: 'Цифровая электросеть', panel: 'Подстанции за 10 минут' },
  mission: ruMission,
  brief: ruBrief,
  guide: ruGuide,
}

const enCopy: LongCopy = {
  careers: { name: 'Open space', lead: 'A planet for people who want to build systems with us. Every opening is a mission of its own.', mission: 'Accept the mission →' },
  home: { title: 'Planet ABV Develop', lead: 'Our team and competencies live here. Around it orbit the department moons, and farther out — the project planets we built. Choose where to fly.', cta: 'Send a flight plan' },
  team: enTeam,
  projects: { platform: 'Distribution platform', grid: 'Digital power grid', panel: 'Substations in 10 min' },
  mission: enMission,
  brief: enBrief,
  guide: enGuide,
}

const long: Record<Lang, LongCopy> = { ru: ruCopy, en: enCopy, de, es, zh, th }

export function getUniverseCopy(lang: Lang): UniverseCopy {
  return { chrome: chrome[lang], egg: eggs[lang], ...long[lang] }
}

export function nebulaFor(lang: Lang) {
  return eggs[lang].nebula
}
